import {
  AssessmentEntry,
  AssessmentEntryScore,
  AssessmentMethodGrade,
  AssessmentsGradeOption,
  isAssessmentEntryGrade,
} from '@schooly/api';
import { CellParams } from '@schooly/components/data-grid';
import { DropdownCell } from '@schooly/style';
import React from 'react';

import { CellColorProps, getCellTextColor } from '../colouredCells';
import { GradeUpdateParams } from '../handlers/gradeHandlers';
import { useMarkbookCellContext } from '../MarkbookCellContext';
import { AssessmentCellState, MarkbookForGroupRow, MarkbookForGroupsRow } from '../types';

type GradeCellRendererProps = {
  method: AssessmentMethodGrade;
  entries: AssessmentEntry[];
  canEdit: boolean;
  groupId: string;
  assessmentId: string;
  subjectId: string;
  options: AssessmentsGradeOption[];
  studentRelationId: string;
  onGradeUpdate: (params: GradeUpdateParams) => Promise<void>;
  getAssessmentEntryColor?: (cellProps: CellColorProps) => string | null;
  gradeControllerProps?: Record<string, unknown>;
  categoryId?: string;
  isDisabled?: boolean;
} & CellParams<MarkbookForGroupRow | MarkbookForGroupsRow, AssessmentCellState>;

export const GradeCellRenderer = ({
  method,
  isDisabled,
  canEdit,
  onGradeUpdate,
  assessmentId,
  groupId,
  subjectId,
  gradeControllerProps,
  studentRelationId,
  entries,
  row,
  field,
  cellState,
  categoryId,
  options,
}: GradeCellRendererProps) => {
  const { getAssessmentEntryColor } = useMarkbookCellContext();

  const entry = entries.find((e) => e.method.id === method.id);

  const relationId = studentRelationId;
  const status = cellState?.status ?? null;
  const color = entry
    ? getAssessmentEntryColor?.({
        studentRelationId,
        assessmentId,
        subjectId,
        groupId,
        entry: entry as AssessmentEntryScore,
        method,
      })
    : null;

  const gradeOption =
    entry && isAssessmentEntryGrade(entry)
      ? options.find((option) => option.id === entry?.select_list_option_id)
      : null;

  return (
    <DropdownCell
      {...gradeControllerProps}
      value={gradeOption ?? null}
      options={options}
      status={status}
      backgroundColor={color}
      isDisabled={isDisabled}
      canEdit={canEdit && !isDisabled}
      textColor={color ? getCellTextColor(color) : undefined}
      onUpdate={(gradeOption) => {
        return onGradeUpdate({
          cellId: `${row.id}_${field}`,
          rowId: row.id,
          field,
          studentId: relationId,
          methodId: method.id ?? '',
          assessmentId,
          selectListOptionId: gradeOption?.id ?? null,
          groupId,
          categoryId,
        });
      }}
    />
  );
};
